// Account statement (§11.2): period filter, money in/out totals, every
// credit and debit grouped by day, and CSV/PDF download.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faDownload } from '@fortawesome/free-solid-svg-icons';
import { listStatement } from '@/api/statement';
import type { StatementEntry } from '@/types/phase2.types';
import { formatIndianCurrency } from '@/utils/currency';
import { buildStatementCsv, buildStatementPdf } from '@/utils/statementFile';
import { downloadBlob } from '@/utils/downloadBlob';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import './statement.css';

type Period = 'THIS_MONTH' | 'LAST_MONTH' | 'LAST_3_MONTHS' | 'CUSTOM';

const PERIODS: Array<{ key: Period; label: string }> = [
  { key: 'THIS_MONTH', label: 'This month' },
  { key: 'LAST_MONTH', label: 'Last month' },
  { key: 'LAST_3_MONTHS', label: 'Last 3 months' },
  { key: 'CUSTOM', label: 'Custom' },
];

const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const endOfDay = (d: Date): number => startOfDay(d) + 86_399_999;

const periodRange = (period: Period, customFrom: string, customTo: string): { from: number; to: number; label: string } => {
  const now = new Date();
  if (period === 'THIS_MONTH') {
    return { from: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), to: now.getTime(), label: 'This month' };
  }
  if (period === 'LAST_MONTH') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: first.getTime(), to: endOfDay(last), label: 'Last month' };
  }
  if (period === 'LAST_3_MONTHS') {
    const first = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    return { from: first.getTime(), to: now.getTime(), label: 'Last 3 months' };
  }
  const from = customFrom ? startOfDay(new Date(`${customFrom}T00:00:00`)) : 0;
  const to = customTo ? endOfDay(new Date(`${customTo}T00:00:00`)) : now.getTime();
  return { from, to, label: customFrom && customTo ? `${customFrom} to ${customTo}` : 'Custom' };
};

const dayHeading = (ts: number): string => {
  const day = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (startOfDay(day) === startOfDay(today)) return 'Today';
  if (startOfDay(day) === startOfDay(yesterday)) return 'Yesterday';
  return day.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Statement = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<StatementEntry[] | null>(null);
  const [period, setPeriod] = useState<Period>('THIS_MONTH');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    listStatement()
      .then((list) => setEntries([...list].sort((a, b) => b.date - a.date)))
      .catch(() => setEntries([]));
  }, []);

  const range = useMemo(() => periodRange(period, customFrom, customTo), [period, customFrom, customTo]);

  const filtered = useMemo(
    () => (entries ?? []).filter((e) => e.date >= range.from && e.date <= range.to),
    [entries, range]
  );

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, e) => {
          if (e.amountRupees >= 0) acc.in += e.amountRupees;
          else acc.out += Math.abs(e.amountRupees);
          return acc;
        },
        { in: 0, out: 0 }
      ),
    [filtered]
  );

  const groups = useMemo(() => {
    const byDay = new Map<string, StatementEntry[]>();
    for (const e of filtered) {
      const key = new Date(e.date).toDateString();
      byDay.set(key, [...(byDay.get(key) ?? []), e]);
    }
    return [...byDay.values()];
  }, [filtered]);

  const download = (kind: 'csv' | 'pdf') => {
    const stamp = new Date().toISOString().slice(0, 10);
    const blob =
      kind === 'csv' ? buildStatementCsv(filtered) : buildStatementPdf(filtered, range.label);
    downloadBlob(blob, `NovaBank-statement-${stamp}.${kind}`);
    toast.success(kind === 'csv' ? 'Statement CSV downloaded' : 'Statement PDF downloaded');
  };

  return (
    <div className="nova-statement-page">
      <div className="nova-pay-flow-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => navigate('/dashboard')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Statement</h2>
      </div>

      <div className="nova-statement-periods" role="tablist" aria-label="Period">
        {PERIODS.map((p) => (
          <button
            type="button"
            key={p.key}
            className={`nova-statement-period ${period === p.key ? 'active' : ''}`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === 'CUSTOM' && (
        <div className="nova-statement-custom">
          <label className="form-label">
            From
            <input
              type="date"
              className="form-control"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
          </label>
          <label className="form-label">
            To
            <input
              type="date"
              className="form-control"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </label>
        </div>
      )}

      {entries && (
        <div className="nova-statement-summary">
          <span>
            Money in <strong className="nova-st-in">{formatIndianCurrency(totals.in)}</strong>
          </span>
          <span>
            Money out <strong className="nova-st-out">{formatIndianCurrency(totals.out)}</strong>
          </span>
          <span className="nova-statement-downloads">
            <Button variant="outline" onClick={() => download('csv')} disabled={filtered.length === 0}>
              <FontAwesomeIcon icon={faDownload} /> CSV
            </Button>
            <Button variant="outline" onClick={() => download('pdf')} disabled={filtered.length === 0}>
              <FontAwesomeIcon icon={faDownload} /> PDF
            </Button>
          </span>
        </div>
      )}

      {!entries && <Loader rows={5} />}
      {entries && filtered.length === 0 && (
        <p className="nova-pay-empty-line">No transactions in this period.</p>
      )}

      {groups.map((dayEntries) => (
        <div key={dayEntries[0].id} className="nova-statement-day">
          <h3 className="nova-statement-day-heading">{dayHeading(dayEntries[0].date)}</h3>
          {dayEntries.map((e) => (
            <div key={e.id} className="nova-statement-row">
              <span className="nova-statement-row-text">
                <span className="nova-statement-desc">{e.description}</span>
                <span className="nova-statement-sub">
                  {[e.counterparty, e.category, e.reference ? `Ref ${e.reference}` : '']
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className={`nova-statement-amount ${e.amountRupees >= 0 ? 'nova-st-in' : 'nova-st-out'}`}>
                {e.amountRupees >= 0 ? '+' : '−'}
                {formatIndianCurrency(Math.abs(e.amountRupees))}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default Statement;
